// OtterBonk's own welcome page (大冒险的欢迎页): walking into the OtterBonk hut opens it full screen over the office. It is
// the game's title screen and lobby in one: the rooms (创建房间 / 加入), my room with everyone's otter dressed as their
// class, the class picker (a portrait of my otter as each class), 准备 / 开始, how to play (and 新手教程: act('tutorial')), my personal best and the
// game's settings (sound, 省电模式). 离开 closes it and the office is back (the otter is still in the hut: the voice there
// goes on, and a small card offers 打开大冒险 again). The run itself still mounts on top of it (client.js) when the
// office says "ob_go"; when the run is left, this page is there again.
// It only draws: what it shows comes from the hut (src/office/games/otterbonk.ts "ob_state" / "ob_best"), what it does
// goes back through the callbacks games-otterbonk.js gives it (the same messages the old hut card sent).
// mountWelcome({ act, myId, lookOf, onClose, touch }) -> { render(state), count(n), show(on), shown, back, primary, pressed, destroy }
import { CLASSES, C_FISHER, SAVE_MS, MAPS, BOSSES, FINAL, mapOf } from './data.js';
import { L, hintText, bossName } from './text.js';
import { GEAR_FILES } from './gear.js';
import { sound } from './sfx.js';
import { shakeSetting, setShakeSetting, SHAKE_KEYS } from './juice.js';
import { t, onLang } from '/i18n.js';
import { icon, put, label } from '/icons.js';
import { buildFrame, lookFor, BODIES, bodyOf, cell16, otterArtReady } from '/looks.js';
import { canPlay, HERO_GOALS, normalizeMilestones } from './progress.js';
const CSS_URL = new URL('./welcome.css', import.meta.url).pathname; // its URL: Vite names the built file by its content (vite.config.mjs)

const MAX = 4; // players in a room
const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const el = (tag, cls = '', text = '') => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };
// each class's gear on the otter (as client.js CLASS_GEAR draws it in the run): the back piece first, then the rest
const CLASS_GEAR = [['fisherHat'], ['chefPan', 'chefHat'], ['diverTank', 'diverMask'], ['engWrench', 'engHat'], ['rockArmor', 'rockFists', 'rockHammer.0'], ['rangerBow', 'rangerHood'], ['bladeSheath', 'bladeSword', 'bladeHat']];
const BACK = new Set(['diverTank', 'rockArmor', 'engWrench']);
/** A room member's class: `cls2` (every class; the hut sends `cls` in the range pages from before 刀客 know). */
const clsOf = (p) => { const c = p?.cls2 ?? p?.cls ?? 0; return CLASSES[c] ? c : 0; };
/** A room's name: worded here (ob.room.name) from its number (`n`, or an older hut's "房间 3"). */
export const roomName = (r) => { const n = r?.n ?? Number(/(\d+)$/.exec(r?.name ?? '')?.[1]); return n ? t('ob.room.name', { n }) : (r?.name ?? ''); };
/** A member's tag on the room card, by their state there. */
const TAGS = { host: { icon: 'crown', text: 'ob.room.tag.host' }, away: { icon: null, text: 'ob.room.tag.away' }, ready: { icon: 'check', text: 'ob.room.tag.ready' }, waiting: { icon: null, text: 'ob.room.tag.waiting' } };
const tagOf = (p, r) => (p.id === r.host ? 'host' : r.busy ? (p.online ? null : 'away') : p.ready ? 'ready' : 'waiting');
const PORTRAIT_DIR = 2; // facing down-right (dir16: 0 down, 4 right): a three-quarter view

/** How to play, the same rules the run's own lobby lists (client.js renderLobby), with the touch controls on a phone. */
export function rulesText(touch) {
  return [
    t(touch ? 'ob.lobby.rule.moveTouch' : 'ob.welcome.rule.move'), t('ob.lobby.rule.hit'), t('ob.welcome.rule.cards'), t('ob.lobby.rule.ghosts'), t('ob.lobby.rule.map'),
    t('ob.lobby.rule.lords'), t('ob.lobby.rule.drops'), t('ob.welcome.rule.portal'), t('ob.welcome.rule.picks'),
  ];
}

// ---------------------------------------------------------------- portraits: my otter (or a teammate's) as a class
const tpls = typeof Image === 'undefined' ? [] : BODIES.map((b) => { const i = new Image(); i.src = b.sheet; return i; });
const tplsReady = Promise.all([otterArtReady, ...tpls.map((i) => (i.complete ? null : new Promise((r) => { i.onload = i.onerror = r; })))]);
const gearCache = new Map(); // body -> Promise<{ img, frames } | null>
function gearFor(body) {
  if (!gearCache.has(body)) {
    const f = GEAR_FILES[body];
    gearCache.set(body, !f ? Promise.resolve(null) : Promise.all([fetch(f.json).then((r) => r.json()), new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = f.png; })])
      .then(([j, img]) => ({ img, frames: j.frames })).catch(() => null));
  }
  return gearCache.get(body);
}
const frames = new Map(); // look json -> its portrait frame (one 32 x 32 cell painted, not the whole sheet); oldest out first
function frameOf(look) {
  const k = JSON.stringify(look);
  let c = frames.get(k);
  if (c) { frames.delete(k); frames.set(k, c); return c; }
  c = buildFrame(tpls, look, cell16('idle', PORTRAIT_DIR, 0)); frames.set(k, c);
  if (frames.size > 32) frames.delete(frames.keys().next().value);
  return c;
}
/** A canvas of the otter in look wearing class cls's gear, size CSS px square (pixelated): drawn once the art is in. */
function portrait(look, cls, size = 64) {
  const c = el('canvas', 'obwpx'); c.width = c.height = 48; c.style.width = c.style.height = size + 'px';
  tplsReady.then(() => gearFor(bodyOf(look))).then((G) => {
    const g = c.getContext('2d'); g.imageSmoothingEnabled = false; g.clearRect(0, 0, 48, 48);
    const ox = 8, oy = 9; // the otter's 32 x 32 cell inside the 48 x 48 canvas (the rod pokes out of it)
    g.drawImage(frameOf(look), ox, oy);
    if (!G) return;
    const slot = PORTRAIT_DIR * 3, layers = CLASS_GEAR[cls] ?? [];
    for (const name of [...layers.filter((l) => BACK.has(l)), ...layers.filter((l) => !BACK.has(l))]) {
      const f = G.frames[name]?.[slot]; if (!f || !f[2]) continue;
      g.drawImage(G.img, f[0], f[1], f[2], f[3], ox + f[4], oy + f[5], f[2], f[3]);
    }
  }).catch(() => {});
  return c;
}

// ---------------------------------------------------------------- the page
export function mountWelcome({ act, myId, lookOf = () => null, onClose, touch = () => false, canInviteGuests = () => false }) {
  /** someone's otter as the office draws it (games.js lookOf: their look, else made from their seed) */
  const lookOfId = (id, p = null) => { const l = lookOf(id) ?? (p && { look: p.look }); return lookFor(l?.look, l?.seed ?? id); };
  if (!document.querySelector(`link[href="${CSS_URL}"]`)) {
    const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = CSS_URL; document.head.appendChild(l);
  }
  const root = el('div', 'obw'); root.style.display = 'none'; document.body.appendChild(root);
  const btn = (text, onclick, cls = '', ic = null) => {
    const b = el('button', cls); if (ic) label(b, ic, text); else b.textContent = text;
    b.onclick = (e) => { sound.unlock(); sound.play('click'); if (e.detail > 0) b.blur(); onclick(e); }; return b; // (a mouse click leaves no focus: Enter is the page's)
  };
  // top: the title, how many are in the hut, 玩法 / 设置 / 离开
  const top = el('div', 'obwtop'), logo = el('div', 'obwlogo');
  const sub = el('span');
  logo.append(icon('sword', { size: 32 }), (() => { const t = el('div'); t.append(el('b', '', 'OtterBonk'), sub); return t; })());
  const count = el('span', 'obwcount');
  const helpBtn = btn('', () => panel(panel.open === 'help' ? null : 'help'), '', 'bulb');
  const setBtn = btn('', () => panel(panel.open === 'set' ? null : 'set'), '', 'gear');
  const outBtn = btn('', () => onClose(), '', 'door');
  // 大百科 (wiki.js): every class, skill, drop and enemy with the game's own numbers
  let wiki = null;
  const openWiki = (tab) => import('./wiki.js').then((m) => { wiki = m; panel(null); m.openWiki({ tab }); }).catch(() => {});
  const wikiBtn = btn('', () => openWiki('cls'), '', 'star');
  // 新手教程: the short run on this device (tutorial.js, the hut mounts it: act('tutorial'))
  const tutBtn = btn('', () => act('tutorial'), '', 'play');
  /** The top row's words (again on a language change). */
  const words = () => {
    sub.textContent = t('ob.common.title');
    for (const [b, ic, k] of [[helpBtn, 'bulb', 'ob.common.help'], [setBtn, 'gear', 'ob.common.settings'], [outBtn, 'door', 'ob.common.leave'], [wikiBtn, 'star', 'ob.common.wiki'], [tutBtn, 'play', 'ob.common.tutorial']]) label(b, ic, t(k));
    outBtn.title = t('ob.welcome.leaveTip'); tutBtn.title = t('ob.welcome.tutorialTip');
    if (count.dataset.n) count.textContent = t('ob.hut.count', { n: Number(count.dataset.n) });
  };
  words();
  const tbtns = el('div', 'obwtbtns'); tbtns.append(tutBtn, helpBtn, wikiBtn, setBtn, outBtn);
  top.append(logo, count, tbtns);
  // body: my room (or the rooms to pick from) on the left, me on the right
  const body = el('div', 'obwbody'), main = el('div', 'obwmain'), side = el('div', 'obwside');
  body.append(main, side);
  const status = el('div', 'obwstatus');
  const over = el('div', 'obwover'), card = el('div', 'obwpanel'); over.append(card);
  over.onclick = (e) => { if (e.target === over) panel(null); };
  root.append(top, status, body, over);

  let S = { rooms: [], best: null, progress: null, heroEpoch: null, note: '' };
  // pointers down on the page (a redraw waits for them, at most 2 s: a press whose release never came, e.g. a context
  // menu that took it, mustn't freeze the page)
  const down = new Map();
  root.addEventListener('pointerdown', (e) => down.set(e.pointerId, Date.now()), true);
  for (const t of ['pointerup', 'pointercancel']) addEventListener(t, (e) => down.delete(e.pointerId), true);
  addEventListener('contextmenu', () => down.clear(), true); addEventListener('blur', (e) => { if (e.target === window) down.clear(); });
  function render(next) {
    if (next) S = next;
    const me = myId(), mine = S.rooms.find((r) => r.players.some((p) => p.id === me)), look = lookOfId(me);
    status.textContent = S.note || (mine ? t(mine.busy ? 'ob.room.busy' : 'ob.welcome.status.inRoom', { room: roomName(mine) }) : t(S.rooms.length ? 'ob.welcome.status.pick' : 'ob.welcome.status.none'));
    status.classList.toggle('note', !!S.note);
    main.replaceChildren();
    if (mine) main.append(roomCard(mine, me, true));
    const others = S.rooms.filter((r) => r !== mine);
    if (!mine) {
      const create = btn(t('ob.welcome.create'), () => act('new'), 'obwprimary obwcreate', 'sword');
      main.append(create);
    }
    if (others.length) {
      const h = el('div', 'obwh', t(mine ? 'ob.welcome.otherRooms' : 'ob.welcome.rooms')); main.append(h);
      const list = el('div', 'obwrooms'); for (const r of others) list.append(roomCard(r, me, false, !!mine)); main.append(list);
    }
    // me: a big portrait as my class, my best, a few lines of how to play
    const myCls = clsOf(mine?.players.find((p) => p.id === me)), c = L(CLASSES[myCls]);
    const hero = el('div', 'obwhero'); hero.append(portrait(look, myCls, 144));
    const heroTxt = el('div', 'obwherotxt'); heroTxt.append(put(el('b'), c.px, c.name), el('span', 'role', c.role), el('p', '', c.blurb));
    hero.append(heroTxt);
    const best = el('div', 'obwbest'), b = S.best;
    best.append(put(el('div', 'obwh'), 'trophy', t('ob.welcome.best.title')));
    if (b?.best) {
      const nums = el('div', 'obwnums'), B1 = b.best;
      const top = B1.stages ? [t('ob.end.bosses', { n: B1.stages, extra: B1.stages - 1 }), t('ob.welcome.best.beaten')] : B1.win ? [t('ob.welcome.best.won'), t('ob.welcome.best.beatBoss')] : [mmss(B1.timeS), t('ob.end.survived')];
      for (const [v, k] of [top, [B1.kills, t('ob.end.col.kills')], ['Lv ' + B1.level, t('ob.end.level')], [`${b.wins}/${b.runs}`, t('ob.welcome.best.winsRuns')]]) {
        const d = el('div'); d.append(el('b', '', String(v)), el('span', '', k)); nums.append(d);
      }
      best.append(nums);
    } else best.append(el('p', 'obwmuted', t('ob.welcome.best.none')));
    const progress = el('div', 'obwbest'); progress.append(put(el('div', 'obwh'), 'star', t('ob.progress.title')));
    if (S.heroEpoch === 2) progress.append(el('p', 'obwreset', t('ob.progress.devReset')));
    if (!S.progress) progress.append(el('p', 'obwmuted', t('ob.progress.unavailable')), btn(t('ob.progress.refresh'), () => act('progress'), 'obwlink'));
    else {
      progress.append(el('p', 'obwmuted', t('ob.progress.achievementRule')));
      if (S.heroEpoch !== 2) progress.append(el('p', 'obwmuted', t(S.progress.legacy ? 'ob.progress.legacy' : 'ob.progress.legacyUnknown')));
      // The starter achievement is completed when the account's adventure progress is created.
      progress.append(el('p', 'obwmuted', t('ob.progress.goalLine', { name: L(CLASSES[C_FISHER]).name, requirement: t('ob.progress.starterAchievement'), status: t('ob.progress.done') })));
      if (S.progress.milestones?.v === 1) {
        const goals = normalizeMilestones(S.progress.milestones);
        for (const g of HERO_GOALS) {
          const requirement = t(`ob.progress.goal.${g.key}`, { n: Math.floor(goals[g.key]), target: g.target });
          progress.append(el('p', 'obwmuted', t('ob.progress.goalLine', { name: L(CLASSES[g.cls]).name, requirement, status: t(canPlay(S.progress, g.cls) ? 'ob.progress.heroUnlocked' : 'ob.progress.heroLocked') })));
        }
      } else progress.append(el('p', 'obwmuted', t('ob.progress.unavailable')));
      progress.append(btn(t('ob.progress.refresh'), () => act('progress'), 'obwlink'));
    }
    const quick = el('div', 'obwquick');
    quick.append(put(el('div', 'obwh'), 'bulb', t('ob.common.help')));
    for (const line of rulesText(touch()).slice(0, 3)) quick.append(el('p', '', line));
    quick.append(btn(t('ob.common.tutorial'), () => act('tutorial'), 'obwlink'), btn(t('ob.welcome.allRules'), () => panel('help'), 'obwlink'), btn(t('ob.common.wiki'), () => openWiki('cls'), 'obwlink'));
    side.replaceChildren(hero, best, progress, quick);
  }

  /** A room: its players (each as their class), and for my room the class picker and my buttons. */
  function roomCard(r, me, isMine, inOther = false) {
    const box = el('div', 'obwroom' + (isMine ? ' mine' : '') + (r.busy ? ' busy' : ''));
    const head = el('div', 'obwroomhead');
    head.append(el('b', '', roomName(r)), el('span', 'obwtag' + (r.busy ? ' busy' : ''), r.busy ? t('ob.room.playing') : `${r.players.length}/${MAX}`));
    box.append(head);
    const who = el('div', 'obwwho');
    for (const p of r.players) {
      const c = L(CLASSES[clsOf(p)]), tag = TAGS[tagOf(p, r)];
      const chip = el('div', 'obwp' + (p.id === r.host ? ' host' : p.ready ? ' ready' : '') + (p.id === me ? ' me' : ''));
      chip.append(portrait(lookOfId(p.id, p), clsOf(p), isMine ? 72 : 48));
      const tx = el('div', 'obwpt'); tx.append(el('b', '', p.id === me ? t('ob.common.me', { name: p.name }) : p.name), put(el('span'), c.px, c.name));
      if (tag) tx.append(put(el('span', 'obwptag'), tag.icon, t(tag.text)));
      chip.append(tx); chip.title = t('ob.welcome.classTip', { name: c.name, role: c.role, blurb: c.blurb }); who.append(chip);
    }
    for (let i = r.players.length; i < MAX && isMine && !r.busy; i++) { const e = el('div', 'obwp empty'); e.append(el('i'), el('span', '', t('ob.room.open'))); who.append(e); }
    box.append(who);
    if (isMine && !r.busy) { // 选职业: each class as a card with my otter wearing it
      const me1 = r.players.find((p) => p.id === me), look = lookOfId(me);
      box.append(el('div', 'obwh', t('ob.welcome.pickClass')));
      const grid = el('div', 'obwclasses');
      CLASSES.forEach((c0, i) => {
        const c = L(c0), b = el('button', 'obwclass' + (clsOf(me1) === i ? ' on' : ''));
        const goal = HERO_GOALS.find((g) => g.cls === i), available = !!S.progress && canPlay(S.progress, i);
        const count = S.progress?.milestones?.v === 1 ? normalizeMilestones(S.progress.milestones) : null;
        const requirement = goal && count ? t(`ob.progress.goal.${goal.key}`, { n: Math.floor(count[goal.key]), target: goal.target }) : t('ob.progress.unavailable');
        const tx = el('div'); tx.append(put(el('b'), c.px, c.name), el('span', 'role', c.role));
        if (S.progress && !available) tx.append(el('span', 'obwlocked', t('ob.progress.heroLocked')));
        tx.append(el('span', 'blurb', available ? c.blurb : requirement));
        b.append(portrait(look, i, 64), tx); b.title = t('ob.welcome.classTip', { name: c.name, role: c.role, blurb: c.blurb });
        if (!available) { b.disabled = true; b.title = requirement; }
        b.onclick = () => { sound.unlock(); sound.play('click'); act('class', i); };
        grid.append(b);
      });
      box.append(grid);
    }
    // (the hint from the members' classes, here in the page's language: the hut's r.hint is in Chinese)
    const hint = r.players.length ? hintText(r.players.map(clsOf)) : null;
    if (hint && !r.busy) box.append(put(el('div', 'obwhint'), 'bulb', hint));
    if (r.last && !r.busy) box.append(put(el('div', 'obwlast'), r.last.win ? 'party' : null, lastLine(r.last)));
    if (r.save && !r.busy) box.append(el('div', 'obwlast', t('ob.welcome.save', { stage: r.save.stage ?? 0, level: (r.save.stage ?? 0) + 1, map: L(MAPS[mapOf(r.save.stage ?? 0)]).name, h: Math.max(1, Math.ceil((r.save.at + SAVE_MS - Date.now()) / 36e5)) })));
    const row = el('div', 'obwbtns');
    if (isMine) {
      if (r.busy) row.append(btn(t('ob.welcome.rejoin'), () => act('rejoin', r), 'obwprimary', 'play'));
      else {
        if (r.host === me) {
          const waiting = r.players.filter((p) => p.id !== me && !p.ready).length;
          if (r.save) { const cont = btn(t('ob.welcome.resume'), () => act('start', true), 'obwprimary', 'play'); cont.disabled = !!waiting; row.append(cont); }
          const go = btn(waiting ? t('ob.welcome.waiting', { n: waiting }) : t(r.save ? 'ob.welcome.restart' : r.last ? 'ob.common.playAgain' : 'ob.lobby.start'), () => act('start', false), r.save ? '' : 'obwprimary', 'play');
          go.disabled = !!waiting; row.append(go);
        } else {
          const ready = r.players.find((p) => p.id === me)?.ready;
          row.append(btn(t(ready ? 'ob.welcome.unready' : 'ob.welcome.ready'), () => act('ready', !ready), ready ? '' : 'obwprimary', 'check'));
        }
        row.append(btn(t('ob.welcome.leaveRoom'), () => act('leaveRoom')));
        // invite: a link straight into this room (members); a guest invite link that does the same (people without Raft)
        if (r.players.length < MAX) { row.append(btn(t('ob.welcome.invite'), () => act('invite', r), '', 'people')); if (canInviteGuests()) row.append(btn(t('ob.welcome.guestInvite'), () => act('guestInvite', r))); }
      }
    } else if (!r.busy && r.players.length < MAX && !inOther) row.append(btn(t('ob.welcome.join'), () => act('join', r.id), 'obwprimary'));
    else if (!r.busy && r.players.length < MAX && inOther) row.append(btn(t('ob.welcome.switch'), () => act('join', r.id)));
    if (row.children.length) box.append(row);
    return box;
  }
  /** The last run's line: how it ended (a LAST_ENDS key), then its numbers. */
  const LAST_ENDS = { home: 'ob.welcome.last.home', after: 'ob.welcome.last.downAfter', win: 'ob.welcome.last.win', timeout: 'ob.welcome.last.timeout', down: 'ob.welcome.last.down' };
  const lastLine = (R) => {
    const end = R.reason === 'home' ? 'home' : R.stages ? 'after' : R.win ? 'win' : R.reason === 'timeout' ? 'timeout' : 'down';
    const cleared = R.stages > 0 ? t('ob.end.cleared', { n: R.stages, boss: bossName(BOSSES[FINAL]), extra: R.stages - 1 }) : '';
    const v = { result: t(LAST_ENDS[end], { cleared }), time: mmss(R.timeS), kills: R.kills, level: R.level, mvp: R.mvp ?? '' };
    return t(R.mvp ? 'ob.welcome.last.lineMvp' : 'ob.welcome.last.line', v);
  };

  /** 玩法 / 设置 over the page (null closes it). */
  function panel(which) {
    panel.open = which;
    over.style.display = which ? 'flex' : 'none';
    helpBtn.classList.toggle('on', which === 'help'); setBtn.classList.toggle('on', which === 'set');
    if (!which) return;
    const close = btn(t('ob.common.close'), () => panel(null), 'obwprimary');
    // the title row stays at the top of a long panel, with its own 关闭 (a phone needn't scroll down to close it)
    const head = (ic, text) => { const h = el('div', 'obwphead'); h.append(put(el('div', 'obwptitle'), ic, text), btn(t('ob.common.close'), () => panel(null))); return h; };
    if (which === 'help') {
      const rules = el('div', 'obwrules'); for (const line of rulesText(touch())) rules.append(el('p', '', line));
      const cls = el('div', 'obwclasslist'), look = lookOfId(myId());
      CLASSES.forEach((c0, i) => { const c = L(c0), d = el('div'), tx = el('div'); tx.append(put(el('b'), c.px, t('ob.welcome.classRole', { name: c.name, role: c.role })), el('span', '', c.blurb)); d.append(portrait(look, i, 48), tx); cls.append(d); });
      const maps = el('p', 'obwmuted', t('ob.welcome.maps', { maps: MAPS.map((m) => t('ob.welcome.mapBoss', { map: L(m).name, boss: bossName(m.boss) })).join(t('ob.wiki.sep')) }));
      card.replaceChildren(head('bulb', t('ob.common.help')), rules, el('div', 'obwh', t('ob.welcome.classes', { n: CLASSES.length })), cls, maps, close);
    } else {
      const S1 = sound.settings;
      const slider = (name, k) => {
        const row = el('label', 'obwrow'), r = el('input'); r.type = 'range'; r.min = 0; r.max = 100; r.value = Math.round(S1[k] * 100);
        const val = el('b', '', r.value);
        r.oninput = () => { sound.unlock(); sound.set(k, r.value / 100); val.textContent = r.value; if (k === 'sfx') sound.play('pearl', { jitter: false }); };
        row.append(el('span', '', name), r, val); return row;
      };
      const mute = btn(t(S1.mute ? 'ob.welcome.set.muted' : 'ob.welcome.set.mute'), () => { sound.set('mute', !sound.settings.mute); panel('set'); }, S1.mute ? 'obwprimary' : '', S1.mute ? 'speaker_off' : 'speaker');
      let lite = null; try { lite = localStorage.getItem('ob.lite'); } catch {}
      const seg = el('div', 'obwseg');
      for (const [v, k] of [[null, 'ob.welcome.set.auto'], ['1', 'ob.welcome.set.on'], ['0', 'ob.welcome.set.off']]) {
        const b = btn(t(k), () => { try { if (v == null) localStorage.removeItem('ob.lite'); else localStorage.setItem('ob.lite', v); } catch {} panel('set'); }, lite === v ? 'on' : '');
        seg.append(b);
      }
      const liteRow = el('div', 'obwrow'); liteRow.append(el('span', '', t('ob.welcome.set.lite')), seg);
      // 震屏 (juice.js): 强 / 弱 / 关 (关 by default when the system asks for less motion)
      const sh = shakeSetting(), seg2 = el('div', 'obwseg');
      for (const v of [2, 1, 0]) seg2.append(btn(t(SHAKE_KEYS[v]), () => { setShakeSetting(v); panel('set'); }, sh === v ? 'on' : ''));
      const shakeRow = el('div', 'obwrow'); shakeRow.append(el('span', '', t('ob.shake.title')), seg2);
      card.replaceChildren(head('gear', t('ob.common.settings')), el('div', 'obwh', t('ob.welcome.set.sound')), slider(t('ob.set.music'), 'music'), slider(t('ob.set.sfx'), 'sfx'), mute,
        el('div', 'obwh', t('ob.welcome.set.graphics')), liteRow, el('p', 'obwmuted', t('ob.welcome.set.liteHint')),
        shakeRow, el('p', 'obwmuted', t('ob.shake.hint')), close);
    }
  }
  panel.open = null; over.style.display = 'none';
  // 设置 → 语言: the top row, the page and an open panel again
  const offLang = onLang(() => { words(); render(); if (panel.open) panel(panel.open); });

  return {
    render,
    count(n) { count.dataset.n = n; const s = t('ob.hut.count', { n }); if (count.textContent !== s) count.textContent = s; },
    pressed: () => { for (const t of down.values()) if (Date.now() - t < 2000) return true; return false; },
    /** Enter: the page's main button (准备 / 开始冒险 / 创建房间 / 加入), unless a panel is up */
    primary() { if (!panel.open && !wiki?.wikiOpen() && !document.querySelector('body > .ob')) main.querySelector('button.obwprimary:not(:disabled)')?.click(); },
    show(on) { const was = root.style.display !== 'none'; root.style.display = on ? '' : 'none'; if (!on && was) { panel(null); down.clear(); if (wiki?.wikiOpen()) wiki.closeWiki(); } document.body.classList.toggle('obw-open', on); },
    shown: () => root.style.display !== 'none',
    /** Esc: a panel first, then the page. */
    back() { if (wiki?.wikiOpen()) wiki.closeWiki(); else if (panel.open) panel(null); else onClose(); },
    destroy() { offLang(); root.remove(); },
    root,
  };
}
